/**
 * The lightweight company model: an organization owns a tree of categories, hands them
 * out through roles, and sees what its people logged against them. No projects, no
 * rates, no approval flow.
 */

/** Invited but not yet joined people still hold a seat and a set of roles. */
export type MemberStatus = 'active' | 'invited';

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  team: string;
  status: MemberStatus;
}

/** A top-level node of the category tree. Mirrors an ActivityType with is_group = true. */
export interface CategoryGroup {
  id: string;
  name: string;
}

/** A category the organization owns — an ActivityType leaf under a group. */
export interface SharedCategory {
  id: string;
  name: string;
  groupId: string;
  color: string;
  archived: boolean;
}

/**
 * A named set of grants. Roles own nothing: the categories stay owned by the company
 * account, so two roles can hand out the same one without duplicating it.
 */
export interface CompanyRole {
  id: string;
  name: string;
  /** Everyone joining the company gets this one. */
  isDefault: boolean;
}

/** Grants one tree node. A group node brings its whole subtree with it. */
export interface RoleGrant {
  roleId: string;
  /** Either a CategoryGroup id or a SharedCategory id. */
  nodeId: string;
}

export interface MemberRole {
  memberId: string;
  roleId: string;
}

/** Hours a person logged per day, index 0 = first day of the window. */
export interface MemberDays {
  memberId: string;
  hours: number[];
}

/** Total hours a person put on a category across the whole window. */
export interface MemberCategoryTotal {
  memberId: string;
  categoryId: string;
  hours: number;
}

export const WORK_DAY_HOURS = 8;
